import React from "react";
import Modal from "../../Modal/Modal";

const ImageModal = ({
  isOpen,
  onClose,
  imageInputType,
  setImageInputType,
  imageUrl,
  setImageUrl,
  imageFile,
  onImageFileChange,
  imageAltText,
  setImageAltText,
  imageWidth,
  setImageWidth,
  imageHeight,
  setImageHeight,
  imageAlignment,
  setImageAlignment,
  isEditingExistingImage,
  onApplyImage,
  onResetImageForm,
  onDragOver,
  onDrop,
}) => {
  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        <h3 className="text-lg font-semibold mb-4">
          {isEditingExistingImage ? "Edit Image" : "Insert Image"}
        </h3>

        {/* Input Type Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Image Source
          </label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="imageInputType"
                value="url"
                checked={imageInputType === "url"}
                onChange={(e) => setImageInputType(e.target.value)}
                className="mr-2"
              />
              URL
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="imageInputType"
                value="file"
                checked={imageInputType === "file"}
                onChange={(e) => setImageInputType(e.target.value)}
                className="mr-2"
              />
              Upload File
            </label>
          </div>
        </div>

        {/* URL Input */}
        {imageInputType === "url" && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Image URL
            </label>
            <input
              type="url"
              placeholder="https://example.com/image.jpg"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>
        )}

        {/* File Upload */}
        {imageInputType === "file" && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload Image
            </label>
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors"
              onDragOver={onDragOver}
              onDrop={onDrop}
            >
              <input
                type="file"
                accept="image/*"
                onChange={(e) => onImageFileChange(e.target.files[0])}
                className="hidden"
                id="imageFileInput"
              />
              <label
                htmlFor="imageFileInput"
                className="cursor-pointer text-blue-500 hover:text-blue-600"
              >
                {imageFile ? (
                  <span>Selected: {imageFile.name}</span>
                ) : (
                  <span>Click to select or drag and drop an image</span>
                )}
              </label>
            </div>
          </div>
        )}

        {/* Alt Text */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Alt Text (for accessibility)
          </label>
          <input
            type="text"
            placeholder="Describe the image"
            value={imageAltText}
            onChange={(e) => setImageAltText(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Dimensions */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Width (px)
            </label>
            <input
              type="number"
              placeholder="Auto"
              value={imageWidth}
              onChange={(e) => setImageWidth(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Height (px)
            </label>
            <input
              type="number"
              placeholder="Auto"
              value={imageHeight}
              onChange={(e) => setImageHeight(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Alignment */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Alignment
          </label>
          <div className="flex gap-4">
            {["left", "center", "right"].map((align) => (
              <label key={align} className="flex items-center">
                <input
                  type="radio"
                  name="imageAlignment"
                  value={align}
                  checked={imageAlignment === align}
                  onChange={(e) => setImageAlignment(e.target.value)}
                  className="mr-2"
                />
                {align.charAt(0).toUpperCase() + align.slice(1)}
              </label>
            ))}
          </div>
        </div>

        {/* Preview */}
        {((imageInputType === "url" && imageUrl) ||
          (imageInputType === "file" && imageFile)) && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Preview
            </label>
            <div className="border border-gray-300 rounded p-2 bg-gray-50">
              <img
                src={imageUrl}
                alt={imageAltText || "Preview"}
                style={{
                  maxWidth: "100%",
                  maxHeight: "200px",
                  width: imageWidth ? `${imageWidth}px` : "auto",
                  height: imageHeight ? `${imageHeight}px` : "auto",
                  display: imageAlignment === "center" ? "block" : "inline",
                  margin: imageAlignment === "center" ? "0 auto" : "0",
                  float:
                    imageAlignment === "left" || imageAlignment === "right"
                      ? imageAlignment
                      : "none",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
                onLoad={(e) => {
                  e.target.style.display = "block";
                }}
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onResetImageForm}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onApplyImage}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            disabled={imageInputType === "url" ? !imageUrl : !imageFile}
          >
            {isEditingExistingImage ? "Update Image" : "Insert Image"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ImageModal;
